import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useLabels from "../../../hooks/useLabels";

import "./styles/index.scss";
import { useEffect, useState } from "react";
import { Label } from "../../FormComponents";
import { useDispatch, useSelector } from "react-redux";
import {
  selectActiveWinery,
  selectAnalysis,
  selectConfigs,
  selectTreatments,
  selectUsers,
} from "../../../context/selectors";
import { setTreatments } from "../../../context/protocols/treatmentsSlice";
import { setAnalysis } from "../../../context/protocols/analysisSlice";
import { format } from "date-fns";
import { CTA } from "../../Generic";
import { NavLink } from "react-router-dom";
import { getProtocols } from "../../../services/utils";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";

const TrackingOperationDetail = ({
  operation = {},
  isClosing = true,
  closeAdditionalInfo,
}) => {
  const activeWinery = useSelector(selectActiveWinery);
  const [getLabel] = useLabels();
  const dispatch = useDispatch();
  const axiosPrivate = useAxiosPrivate();
  const users = useSelector(selectUsers);
  const configs = useSelector(selectConfigs);
  const analysis = useSelector(selectAnalysis);
  const treatments = useSelector(selectTreatments);
  const [shouldHide, setShouldHide] = useState(isClosing); // internal control

  useEffect(() => {
    if (!isClosing) {
      // delay removing .hide to let the transition play
      const timeout = setTimeout(() => {
        setShouldHide(false);
      }, 10); // enough to let initial render apply .hide

      return () => clearTimeout(timeout);
    } else {
      setShouldHide(true); // when closing, apply hide immediately
    }
  }, [isClosing]);

  const initData = async () => {
    const promiseArray = [];
    if (analysis?.length === 0) {
      promiseArray.push(loadAnalysis());
    }
    if (treatments?.length === 0) {
      promiseArray.push(loadTreatments());
    }

    return Promise.all(promiseArray);
  };

  const loadAnalysis = async () => {
    const currentAnalysis = await getProtocols("analysis", axiosPrivate);
    dispatch(setAnalysis(currentAnalysis));
  };

  const loadTreatments = async () => {
    const currentTreatments = await getProtocols("treatment", axiosPrivate);
    dispatch(setTreatments(currentTreatments));
  };

  useEffect(() => {
    initData();
  }, []);

  if (!operation) return;

  const getSubstances = (opType, trs = []) => {
    const subsBySource = {}; // Oggetto con chiave tank_id o batch_id
    const processedIds = new Set();

    for (const tr of trs) {
      const srcName = tr?.tank_name || tr?.batch_name;
      if (!srcName) continue;

      if (opType === "TREATMENT") {
        if (!subsBySource[srcName]) subsBySource[srcName] = [];
        subsBySource[srcName].push({
          substance_name: tr?.substance_name,
          quantity: tr?.quantity,
          unit: tr?.unit || "",
        });
      } else {
        if (processedIds.has(srcName)) continue;
        processedIds.add(srcName);

        const results = tr?.results || [];
        const ans = results.map((res) => {
          const parent = trs.find((p) => p.substance_id === res.substance_id);
          return {
            substance_name: getLabel(`substance_${parent?.substance_name}`),
            quantity: res?.value,
            unit: parent?.unit ?? null,
          };
        });

        if (ans.length) {
          subsBySource[srcName] = ans;
        }
      }
    }

    return subsBySource;
  };

  const renderData = () => {
    const opType = operation?.operation_type || "";
    if (!opType) return;
    const isTreatments = ["ANALYSIS", "TREATMENT"].includes(opType);

    const trs = operation?.treatments || [];
    if (trs?.length === 0 && isTreatments) return;

    const exam = trs?.[0] || {};
    const analysisProtocol =
      analysis?.find((a) => a?.protocols_id === exam?.protocol_id) || {};
    const subs = getSubstances(opType, trs);
    const buyers =
      opType === "CLOSING"
        ? (operation?.transfers || [])?.map((t) => {
            return {
              quantity: t?.quantity,
              close_dest: t?.destination_buyer_name,
              close_reason: t?.destination_buyer_reason,
            };
          })
        : [];

    return (
      <div className={`additional_info_container ${shouldHide ? "hide" : ""}`}>
        <p className="close_additional" onClick={() => closeAdditionalInfo()}>
          <FontAwesomeIcon
            icon={"fa-arrow-left"}
            size={"xl"}
            className="icon"
          />{" "}
        </p>
        <h5>{getLabel(`operationType${opType}`)}</h5>

        <div className="additionl_info">
          <div className="additionl_info_row">
            <Label label={getLabel("insertionDate")}>
              {format(
                new Date(operation?.insertion_date || null),
                configs.shortDateFormat
              )}
            </Label>
            <Label label={getLabel("operationPriority")}>
              {getLabel(`operationPriority${operation?.priority}`)}
            </Label>
            <Label label={getLabel("operationCompletionUser")}>
              <div>
                {users?.find?.(
                  (user) => Number(user.id) === Number(operation?.completed_by)
                )?.username || ""}
              </div>
            </Label>
            {opType !== "ANALYSIS" && (
              <Label label={getLabel("operationCompletionDate")}>
                {format(
                  new Date(operation?.completion_date || null),
                  configs.dateFormat
                )}
                {new Date(operation?.completion_date || null) >
                  new Date(operation?.expire_date || null) && <> &#10071;</>}
              </Label>
            )}
            {opType !== "ANALYSIS" &&
              new Date(operation?.completion_date || null) >
                new Date(operation?.expire_date || null) && (
                <Label label={getLabel("expireDate")}>
                  {format(
                    new Date(operation?.expire_date || null),
                    configs.shortDateFormat
                  )}
                </Label>
              )}
            {isTreatments && (
              <Label label={getLabel("analysisProtocols")}>
                {analysisProtocol?.protocols_name}
              </Label>
            )}
          </div>
          {opType === "BOTTLING" && (
            <div className="additionl_info_row">
              <Label label={getLabel("protocolBottling")}>
                {format(
                  new Date(operation?.insertion_date || null),
                  configs.shortDateFormat
                )}
              </Label>
            </div>
          )}
          {opType === "CLOSING" && (
            <div className="additionl_info_row">
              <div className="additional_info_substances">
                <span className="label">{getLabel("toCloseDest")}</span>
                {buyers?.map((buyer, index) => {
                  return (
                    <div className="analysis" key={`buyer-${index}`}>
                      <p className="src_name">{buyer?.close_dest}</p>
                      <div className="closing_wrapper">
                        <span>
                          {getLabel(`buyReason_${buyer?.close_reason}`)}
                        </span>
                        <p className="buyer_quantity">{buyer?.quantity || '-'} L</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {["ANALYSIS", "TREATMENT"].includes(opType) && (
            <>
              <div className="additionl_info_row">
                <Label label={getLabel("dateCollection")}>
                  {format(
                    new Date(exam?.collection_date) || null,
                    configs.hourMediumDateFormat
                  )}
                  {new Date(operation?.collection_date || null) >
                    new Date(operation?.expire_date || null) && <> &#10071;</>}
                </Label>

                {new Date(operation?.collection_date || null) >
                  new Date(operation?.expire_date || null) && (
                  <Label label={getLabel("expireDate")}>
                    {format(
                      new Date(operation?.expire_date || null),
                      configs.shortDateFormat
                    )}
                  </Label>
                )}
                <Label label={getLabel("analysisAnalyst")}>
                  {exam?.analyst}
                </Label>
              </div>
              <div className="additionl_info_row">
                <div className="additional_info_substances">
                  <span className="label">
                    {opType === "ANALYSIS"
                      ? getLabel("analysisResults")
                      : getLabel("protocolTreatmentSubstancesUsed")}
                  </span>
                  {Object.keys(subs)?.map((srcName, index) => {
                    return (
                      <div className="analysis" key={`treatment-${index}`}>
                        <p className="src_name">{srcName}</p>
                        <ul className="analysis-list">
                          {subs?.[srcName]?.map((res, i) => {
                            return (
                              <li className="par" key={`treatment-li-${i}`}>
                                <span className="bold">
                                  {res?.substance_name}
                                </span>{" "}
                                <span className="analysis_value">
                                  {`${res?.quantity || "-"} ${res?.unit || ""}`}
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          <div className="additionl_info_row cta_wrapper">
            <NavLink to={`/programming/history/${opType?.toLowerCase?.()}/${operation?.operation_id}`}>
              <CTA customClassName={"add"}>
                {getLabel("goToOperationDetail")}
              </CTA>
            </NavLink>
          </div>
        </div>
      </div>
    );
  };

  return renderData();
};

export default TrackingOperationDetail;
